import { v4 as uuid } from 'uuid'
import type { ReactNode } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent, type MouseEvent } from 'react'
import { Alert, Button, Card, Collapse, Drawer, Dropdown, Empty, Form, Input, Modal, Popconfirm, Space, Tooltip, type MenuProps } from 'antd'
import { DeleteOutlined, EditOutlined, FolderOutlined, HolderOutlined, MoreOutlined, PlusOutlined, SettingOutlined } from '@ant-design/icons'
import { DragDropProvider, DragOverlay, useDroppable, type DragDropEventHandlers } from '@dnd-kit/react'
import { useSortable } from '@dnd-kit/react/sortable'
import { SortableKeyboardPlugin } from '@dnd-kit/dom/sortable'
import { move } from '@dnd-kit/helpers'
import type { useConfiguration } from '../hooks/useConfiguration'
import type { EditorMetadata } from '../contracts'
import { Icon, IconPicker } from './Icon'
import { FIXED_ENTRIES, categoryPresentation } from '../config/presentation'

type Model = ReturnType<typeof useConfiguration>
type Entry = EditorMetadata['entries'][number]
type Category = EditorMetadata['categories'][number]
type Dialog = { kind: 'new_category' }
  | { kind: 'rename_file'; entry: Entry } | { kind: 'rename_category'; category: Category }
type Fields = { label: string; directory?: string; icon_key?: string }
const ICONS = { plus: <PlusOutlined />, folder: <FolderOutlined />, more: <MoreOutlined />, edit: <EditOutlined />,
  remove: <DeleteOutlined />, settings: <SettingOutlined />, drag: <HolderOutlined /> }
const TITLES = { new_category: '新增分类', rename_file: '修改显示名称', rename_category: '分类设置' }
const CONFIRM_TITLES = { new_category: '创建分类', rename_file: '保存名称', rename_category: '保存设置' }
const MENU_TRIGGER: ('click')[] = ['click']
const FILE_RULES = [{ required: true, whitespace: true, message: '请填写文件名' }]
const LABEL_RULES = [{ required: true, whitespace: true, message: '请填写名称' }]
// React owns cross-category DOM changes; the library retains keyboard and overlay feedback.
const SORTABLE_PLUGINS = [SortableKeyboardPlugin]

const CategoryAdd = ({ category, add, disabled }: { category: Category; add: (key: string) => void; disabled: boolean }) => {
  const click = useCallback((event: MouseEvent<HTMLElement>) => {
    event.stopPropagation(); add(category.key)
  }, [add, category.key])
  return <Tooltip title="新增文件"><Button type="text" size="small" icon={ICONS.plus}
    aria-label={'新增文件到' + category.title} onClick={click} disabled={disabled} /></Tooltip>
}

/** Form validation may finish after cancellation; only a live form may add to the shared draft. */
const FileCreation = ({ category, model, close, configure }: {
  category: Category; model: Model; close: () => void; configure: (category: Category) => void;
}) => {
  const [error, setError] = useState<string | null>(null)
  const [form] = Form.useForm<{ name: string; label: string }>()
  const name = Form.useWatch('name', form) ?? ''
  const active = useRef(true)
  const submitting = useRef(false)
  const { createFile, saving } = model
  useEffect(() => { active.current = true; return () => { active.current = false } }, [])
  const cancel = useCallback(() => { if (!submitting.current) { active.current = false; close() } }, [close])
  const settings = useCallback(() => configure(category), [category, configure])
  const keyDown = useCallback((event: KeyboardEvent<HTMLFormElement>) => {
    if (event.key !== 'Escape' || event.nativeEvent.isComposing) return
    event.stopPropagation(); cancel()
  }, [cancel])
  const submit = useCallback(async (fields: { name: string; label: string }) => {
    if (!active.current || saving || submitting.current) return
    submitting.current = true; setError(null)
    try { await createFile(fields.name, '', fields.label, category.key); if (active.current) close() }
    catch (failure) { if (active.current) setError((failure as Error).message) }
    finally { submitting.current = false }
  }, [category.key, close, createFile, saving])
  const prefix = category.directory === '.' ? '' : (category.directory ?? '') + '/'
  if (category.directory == null) return <div className="instance-administration__catalog-create">
    <Alert title="请先配置分类目录" type="info" showIcon />
    <div className="instance-administration__catalog-create-actions"><Button size="small" onClick={cancel}>取消</Button>
      <Button size="small" type="primary" onClick={settings}>设置目录</Button></div>
  </div>
  return <Form form={form} layout="vertical" name={'configuration-file-create-' + category.key} aria-label={'在' + category.title + '中新增文件'}
    onFinish={submit} onKeyDown={keyDown} className="instance-administration__catalog-create" disabled={saving}>
    <Form.Item name="name" label="文件名" rules={FILE_RULES}>
      <Input placeholder="rules.yaml" autoComplete="off" autoFocus />
    </Form.Item>
    <output aria-label="最终相对路径" className="instance-administration__catalog-target">{name ? prefix + name : prefix || './'}</output>
    <Form.Item name="label" label="显示名称" rules={LABEL_RULES}><Input autoComplete="off" maxLength={120} /></Form.Item>
    {error ? <Alert title={error} type="error" role="alert" showIcon /> : null}
    <div className="instance-administration__catalog-create-actions"><Button size="small" onClick={cancel}>取消</Button>
      <Button size="small" type="primary" htmlType="submit">创建文件</Button></div>
  </Form>
}

const FileRow = ({ entry, index, model, rename }: { entry: Entry; index: number; model: Model; rename: (entry: Entry) => void }) => {
  const [error, setError] = useState<string | null>(null)
  const live = useRef(true)
  useEffect(() => { live.current = true; return () => { live.current = false } }, [])
  const persist = useCallback(async (operation: () => Promise<unknown>) => {
    setError(null)
    try { await operation() } catch (failure) { if (live.current) setError((failure as Error).message) }
  }, [])
  const { selectFile, deleteFile, editCatalog } = model
  const fixed = FIXED_ENTRIES.has(entry.path)
  const data = useMemo(() => ({ category_key: entry.category_key }), [entry.category_key])
  const { ref, handleRef, isDragging } = useSortable({ id: entry.path, group: entry.category_key, index, data, disabled: model.saving || fixed, plugins: SORTABLE_PLUGINS })
  const select = useCallback(() => selectFile(entry.path), [entry.path, selectFile])
  const remove = useCallback(() => persist(() => deleteFile(entry.path)), [entry.path, deleteFile, persist])
  const renameEntry = useCallback(() => rename(entry), [entry, rename])
  const items = useMemo<MenuProps['items']>(() => [
    { key: 'rename', label: '修改显示名称', icon: ICONS.edit },
    ...(!fixed ? [{ key: 'move', label: '移到分类', icon: ICONS.folder,
      children: model.catalog?.categories.map((category) => ({ key: 'category:' + category.key, label: category.title })) }] : []),
  ], [fixed, model.catalog?.categories])
  const menuClick = useCallback<NonNullable<MenuProps['onClick']>>(({ key }) => {
    if (key === 'rename') { renameEntry(); return }
    if (!fixed && key.startsWith('category:')) void persist(() => editCatalog({ kind: 'move_file', path: entry.path, category_key: key.slice(9) }))
  }, [editCatalog, entry.path, fixed, persist, renameEntry])
  const menu = useMemo(() => ({ items, onClick: menuClick }), [items, menuClick])
  return <><div ref={ref} className={`instance-administration__catalog-file${model.file?.path === entry.path ? ' is-selected' : ''}${isDragging ? ' is-dragging' : ''}`}>
    {!fixed ? <button ref={handleRef} className="instance-administration__catalog-handle" type="button" aria-label={'拖动' + entry.label} disabled={model.saving}>{ICONS.drag}</button> : null}
    <Tooltip title={entry.path}><button type="button" className="instance-administration__catalog-select" onClick={select}
      aria-label={entry.label} aria-pressed={model.file?.path === entry.path}><Icon iconKey={entry.icon_key} /><span>{entry.label}</span></button></Tooltip>
    <Dropdown menu={menu} trigger={MENU_TRIGGER} disabled={model.saving}>
      <Button type="text" size="small" icon={ICONS.more} aria-label={'管理' + entry.label} />
    </Dropdown>
    <Popconfirm title={'删除“' + entry.label + '”？'} okText="删除" cancelText="取消" onConfirm={remove}>
      <Button type="text" size="small" icon={ICONS.remove} aria-label={'删除' + entry.label} disabled={model.saving} />
    </Popconfirm>
  </div>{error ? <Alert title={error} type="error" showIcon role="alert" /> : null}</>
}

const CategoryFiles = ({ category, entries, model, rename, creating, close, configure }: {
  category: Category; entries: Entry[]; model: Model; rename: (entry: Entry) => void; creating: boolean; close: () => void;
  configure: (category: Category) => void;
}) => {
  const data = useMemo(() => ({ category_key: category.key }), [category.key])
  const { ref, isDropTarget } = useDroppable({ id: 'category:' + category.key, data, disabled: model.saving })
  return <div ref={ref} className={`instance-administration__catalog-group${isDropTarget ? ' is-drop-target' : ''}`}>
    {creating ? <FileCreation category={category} model={model} close={close} configure={configure} /> : null}
    {entries.map((entry, index) => <FileRow key={entry.path} entry={entry} index={index} model={model} rename={rename} />)}
    {!entries.length && !creating ? <div className="instance-administration__catalog-empty">暂无文件</div> : null}
  </div>
}

const CategoryRow = ({ category, model, rename }: { category: Category; model: Model; rename: (category: Category) => void }) => {
  const [error, setError] = useState<string | null>(null)
  const live = useRef(true)
  useEffect(() => { live.current = true; return () => { live.current = false } }, [])
  const { editCatalog } = model
  const edit = useCallback(() => rename(category), [category, rename])
  const remove = useCallback(async () => {
    setError(null)
    try { await editCatalog({ kind: 'delete_category', key: category.key }) }
    catch (failure) { if (live.current) setError((failure as Error).message) }
  }, [category.key, editCatalog])
  return <><div className="instance-administration__catalog-category-row">
    <span className="instance-administration__catalog-category-label"><span><Icon iconKey={category.icon_key} fallback="folder" /> {category.title}</span>
      <small>{category.directory ?? '未配置'}</small></span>
    <Space><Button type="text" icon={ICONS.edit} aria-label={'修改' + category.title} onClick={edit} disabled={model.saving} />
      <Popconfirm title="删除分类？文件会保留在未分类中。" okText="删除分类" cancelText="取消" onConfirm={remove}>
        <Button type="text" icon={ICONS.remove} aria-label={'删除分类' + category.title} disabled={model.saving || category.key === 'uncategorized'} />
      </Popconfirm></Space>
  </div>{error ? <Alert title={error} type="error" showIcon role="alert" /> : null}</>
}

/** Catalog actions await the existing apply owner; content drafts never enter their commands. */
export const FileCatalog = ({ model, workflows }: { model: Model; workflows?: ReactNode }) => {
  const [dialog, setDialog] = useState<Dialog | null>(null)
  const [managing, setManaging] = useState(false)
  const [creatingIn, setCreatingIn] = useState<string | null>(null)
  const [collapsed, setCollapsed] = useState(new Set<string>())
  const [error, setError] = useState<string | null>(null)
  const [form] = Form.useForm<Fields>()
  const epoch = useRef(0)
  const submitting = useRef(false)
  useEffect(() => () => { epoch.current += 1 }, [])
  const { editCatalog } = model
  const close = useCallback(() => { epoch.current += 1; setDialog(null); setError(null); form.resetFields() }, [form])
  const cancel = useCallback(() => { if (!submitting.current) close() }, [close])
  const open = useCallback((value: Dialog, fields: Partial<Fields>) => {
    if (submitting.current) return
    epoch.current += 1
    setError(null); form.resetFields(); form.setFieldsValue(fields); setDialog(value)
  }, [form])
  const addFile = useCallback((key: string) => {
    setCreatingIn(key); setCollapsed((previous) => new Set([...previous].filter((value) => value !== key)))
  }, [])
  const closeCreate = useCallback(() => setCreatingIn(null), [])
  const addCategory = useCallback(() => open({ kind: 'new_category' }, {}), [open])
  const renameFile = useCallback((entry: Entry) => open({ kind: 'rename_file', entry }, { label: entry.label, icon_key: entry.icon_key ?? undefined }), [open])
  const renameCategory = useCallback((category: Category) => open({ kind: 'rename_category', category }, {
    label: category.title, directory: category.directory ?? undefined, icon_key: category.icon_key ?? undefined,
  }), [open])
  const openManager = useCallback(() => setManaging(true), [])
  const closeManager = useCallback(() => setManaging(false), [])
  const submit = useCallback(async () => {
    const currentEpoch = epoch.current
    const fields = await form.validateFields().catch(() => null)
    if (!fields || !dialog || currentEpoch !== epoch.current || submitting.current) return
    const actions = {
      new_category: () => editCatalog({ kind: 'add_category', key: uuid(), title: fields.label, directory: fields.directory, icon_key: fields.icon_key }),
      rename_file: () => {
        if (dialog.kind === 'rename_file') return editCatalog({ kind: 'rename_file', path: dialog.entry.path, label: fields.label, icon_key: fields.icon_key })
      },
      rename_category: () => {
        if (dialog.kind === 'rename_category') return editCatalog({ kind: 'rename_category', key: dialog.category.key,
          title: fields.label, directory: fields.directory, icon_key: fields.icon_key })
      },
    }
    submitting.current = true; setError(null)
    try { await actions[dialog.kind](); if (currentEpoch === epoch.current) close() }
    catch (failure) { if (currentEpoch === epoch.current) setError((failure as Error).message) }
    finally { submitting.current = false }
  }, [close, dialog, editCatalog, form])
  const groupedFiles = useMemo(() => Object.fromEntries((model.catalog?.categories ?? []).map((category) => [
    'category:' + category.key, model.catalog?.entries.filter((entry) => entry.category_key === category.key).map((entry) => entry.path) ?? [],
  ])), [model.catalog])
  const dragEnd = useCallback<DragDropEventHandlers['onDragEnd']>(async (event) => {
    const { operation, canceled } = event
    const { source, target } = operation
    if (canceled || !source || !target || model.saving) return
    const path = String(source.id)
    if (FIXED_ENTRIES.has(path)) return
    const reordered = move(groupedFiles, event)
    if (reordered === groupedFiles) return
    const category = model.catalog?.categories.find(({ key }) => reordered['category:' + key].includes(path))
    if (!category) return
    const siblings = reordered['category:' + category.key]
    const currentEpoch = epoch.current
    setError(null)
    try { await editCatalog({ kind: 'move_file', path, category_key: category.key, before_path: siblings[siblings.indexOf(path) + 1] }) }
    catch (failure) { if (currentEpoch === epoch.current) setError((failure as Error).message) }
  }, [editCatalog, groupedFiles, model.catalog?.categories, model.saving])
  const dragPreview = useCallback((source: { id: string | number }) => {
    const entry = model.catalog?.entries.find(({ path }) => path === source.id)
    return entry ? <div className="instance-administration__catalog-preview"><Icon iconKey={entry.icon_key} /><span>{entry.label}</span></div> : null
  }, [model.catalog?.entries])
  const directEntries = useMemo(() => model.catalog?.entries.filter((entry) => FIXED_ENTRIES.has(entry.path)) ?? [], [model.catalog?.entries])
  const items = useMemo(() => model.catalog?.categories.flatMap((category) => {
    const entries = model.catalog?.entries.filter((entry) => entry.category_key === category.key && !FIXED_ENTRIES.has(entry.path)) ?? []
    const policy = categoryPresentation(category.key)
    if (!policy.addFiles && !entries.length) return []
    return [{ key: category.key,
      label: <span className="instance-administration__catalog-category"><Icon iconKey={category.icon_key} fallback="folder" /><span>{category.title}</span>
        <small>{entries.length}</small></span>,
      extra: policy.addFiles ? <CategoryAdd category={category} add={addFile} disabled={model.saving} /> : undefined,
      children: <CategoryFiles category={category} entries={entries}
        model={model} rename={renameFile} creating={creatingIn === category.key} close={closeCreate} configure={renameCategory} />,
    }]
  }) ?? [], [addFile, closeCreate, creatingIn, model, renameCategory, renameFile])
  const expanded = useMemo(() => model.catalog?.categories.filter(({ key }) => !collapsed.has(key)).map(({ key }) => key) ?? [], [collapsed, model.catalog?.categories])
  const changeExpanded = useCallback((keys: string | string[]) => {
    const openKeys = new Set(Array.isArray(keys) ? keys : [keys])
    setCollapsed(new Set(model.catalog?.categories.filter(({ key }) => !openKeys.has(key)).map(({ key }) => key)))
  }, [model.catalog?.categories])
  const okButtonProps = useMemo(() => ({ disabled: model.saving }), [model.saving])
  const actions = <Tooltip title="分类管理"><Button type="text" icon={ICONS.settings} aria-label="分类管理" onClick={openManager} disabled={!model.catalog || model.saving} /></Tooltip>
  return <Card title="配置文件" extra={actions} className="instance-administration__file-list">
    {error && !dialog ? <Alert title={error} type="error" showIcon role="alert" /> : null}
    <DragDropProvider onDragEnd={dragEnd}>
      <div className="instance-administration__catalog-direct">
        {directEntries.map((entry, index) => entry.path === 'workflow.yaml' && workflows
          ? <div key={entry.path}>{workflows}</div>
          : <FileRow key={entry.path} entry={entry} index={index} model={model} rename={renameFile} />)}
      </div>
      {!directEntries.some((entry) => entry.path === 'workflow.yaml') ? workflows : null}
      {items.length ? <Collapse ghost activeKey={expanded} onChange={changeExpanded} items={items} className="instance-administration__catalog" />
        : !directEntries.length ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无配置文件" /> : null}
      <DragOverlay>{dragPreview}</DragOverlay>
    </DragDropProvider>
    <Drawer title="分类管理" open={managing} onClose={closeManager} size={360}>
      {model.catalog?.categories.map((category) => <CategoryRow key={category.key} category={category} model={model} rename={renameCategory} />)}
      <Button block icon={ICONS.plus} onClick={addCategory} disabled={model.saving}>新增分类</Button>
    </Drawer>
    <Modal title={dialog ? TITLES[dialog.kind] : ''} open={!!dialog} onCancel={cancel} onOk={submit}
      okText={dialog ? CONFIRM_TITLES[dialog.kind] : '保存名称'} cancelText="取消" okButtonProps={okButtonProps} cancelButtonProps={okButtonProps}
      confirmLoading={model.saving} closable={!model.saving} mask={{ closable: !model.saving }}>
      {error ? <Alert title={error} type="error" showIcon role="alert" /> : null}
      <Form form={form} layout="vertical" name="configuration-file" preserve={false}>
        <Form.Item name="label" label={dialog?.kind === 'rename_file' ? '显示名称' : '分类名称'}
          rules={LABEL_RULES}><Input autoComplete="off" maxLength={120} /></Form.Item>
        <Form.Item name="icon_key" label="图标"><IconPicker disabled={model.saving} /></Form.Item>
        {dialog?.kind !== 'rename_file' ? <Form.Item name="directory" label="相对目录">
          <Input placeholder="未配置，. 表示配置包根目录" autoComplete="off" />
        </Form.Item> : null}
      </Form>
    </Modal>
  </Card>
}
